import { cn } from "@gymflow/helpers";
import { ReactNode, useCallback, useState } from "react";

import { Button } from "../Button";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";
import { Spinner } from "../Spinner";

export type MenuPopoverOptionType = {
  id: string;
  label: string | ReactNode;
  classNameLabel?: string;
  icon?: ReactNode;
  onClick: () => void | Promise<void>;
  topDivider?: boolean;
};

type AlignType = "start" | "end";

type SideType = "top" | "bottom";

type MenuPopoverProps = {
  classNameTrigger?: string;
  triggerButton: ReactNode;
  options: MenuPopoverOptionType[];
  side?: SideType;
  classNameContent?: string;
  align?: AlignType;
  disabled?: boolean;
  onOpenChange?: (value: boolean) => void;
  isLoading?: boolean;
  hidden?: boolean;
};

export const MenuPopover = ({
  classNameTrigger,
  triggerButton,
  options,
  side = "bottom",
  align = "end",
  classNameContent,
  disabled = false,
  onOpenChange,
  isLoading = false,
  hidden = false,
}: MenuPopoverProps) => {
  const [isOpen, _setIsOpen] = useState(false);

  const setIsOpen = useCallback(
    (value: boolean) => {
      _setIsOpen(value);
      onOpenChange?.(value);
    },
    [onOpenChange],
  );

  return (
    <Popover onOpenChange={(open) => setIsOpen(open)} open={isOpen}>
      <PopoverTrigger
        className={classNameTrigger}
        asChild
        disabled={disabled}
        hidden={hidden}
      >
        {triggerButton}
      </PopoverTrigger>
      <PopoverContent
        side={side}
        className={cn(
          "flex w-40 flex-col gap-0.5 rounded-[0.625rem] border-gray-200 py-2 dark:border-gray-600",
          classNameContent,
        )}
        align={align}
      >
        {isLoading ? (
          <Spinner />
        ) : (
          options.map(
            ({ id, icon, label, onClick, classNameLabel, topDivider }) => (
              <div key={id} className="flex flex-col">
                {topDivider && (
                  <div className="my-0.5 h-px w-full bg-gray-300 dark:bg-gray-700" />
                )}
                <Button
                  intent="transparent"
                  className="mx-2 h-8 min-h-0 justify-start gap-2 !rounded-[0.625rem] bg-transparent p-2.5 py-0"
                  onClick={async () => {
                    await onClick();
                    setIsOpen(false);
                  }}
                >
                  {icon}
                  <div
                    className={cn(
                      "text-gray-500 text-sm font-medium dark:text-gray-400 w-full text-start",
                      classNameLabel,
                    )}
                  >
                    {label}
                  </div>
                </Button>
              </div>
            ),
          )
        )}
      </PopoverContent>
    </Popover>
  );
};
